/**
 * Integrations Hub — the unified integrations + AI-governance page (mounts at
 * `/integrations`; the old `/control-center` route redirects here).
 *
 * One page for everything "what is connected and what may AI do with it".
 * Layout follows the Log Center / Team page language: page title, a brand
 * selector bar, a row of stat tiles, then a pill tab strip switching between
 * three bodies — rendered one at a time:
 *
 *   - Platforms   (`?tab=integrations`) — the per-platform connect grid (the
 *                 `MCP` logo-card grid via `IntegrationsGrid`). The Meta
 *                 "System User token" flow is embedded on the Facebook card.
 *                 OAuth callbacks round-trip back to `/integrations` carrying
 *                 `?code`/`?state`, which this grid reads on mount — that is
 *                 why the hub MUST live at `/integrations`.
 *   - MCP Servers (`?tab=governance`) — the connected-servers list with the
 *                 per-write-tool policy control (allow / require-approval /
 *                 block) + the add-custom-server-by-URL flow (introspects and
 *                 classifies tools). A custom server can use OAuth: the user
 *                 is sent to the provider to sign in, and central-api's
 *                 callback returns to this tab with `?mcp_oauth=…`, which is
 *                 read here (not in the Platforms grid, which isn't mounted).
 *   - Tool Access (`?tab=tool-access`) — per-employee × per-tool grants.
 *
 * Tab KEYS are unchanged (`integrations` / `governance` / `tool-access`) so
 * old deep-links (`/control-center?tab=…` → redirect) keep resolving; only the
 * visible labels changed.
 *
 * Built in the legacy SCSS style — no MUI. Custom card lists, the shared
 * `Popup` for the add-custom form, `MainSquareButton` for actions, Redux
 * `setMessage` toasts. RBAC: the page is browsable by anyone scoped to the
 * shop, but every mutation gates on `integrations.manage` (owner/team_lead/
 * manager). analyst & read_only see read-only status.
 *
 * Scope: the user's currently active shop (`profile.active_shop_slug`). Read
 * live from the store so shop-switcher updates propagate.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import '../../styles/pages/control-center/controlCenter.scss';
// Phase 4 — Tool Access tab styles (per-tool, per-employee grid).
import '../../styles/pages/control-center/toolAccess.scss';
// GDPR tab styles (per-MCP PII category settings).
import '../../styles/pages/control-center/gdprTab.scss';
// Reuse the legacy MCP/Integrations card-grid wrapper (`.integrationContainer`)
// so the connection cards lay out identically to the Integrations hub.
import '../../styles/pages/MCP/mcpConfigBox.scss';
import { platformDisplayName } from '../integrations/MCP/stripe-reporting';

import PageWrapperMain from '../../components/page-wrappers/PageWrapperMain';
import SubPageContentHolder from '../../components/subPageContentHolder/SubPageContentHolder';
import SubPageInnerContentHolder from '../../components/subPageContentHolder/SubPageInnerContentHolder';
import MainSquareButton from '../../ui/buttons/MainSquareButton';
import Popup from '../../ui/popup/Popup';
import LoadingSpinner from '../../ui/loadingSpinner/LoadingSpinner';
import TeachingEmptyState from '../../components/emptyState/TeachingEmptyState';
import IntegrationsGrid from '../integrations/IntegrationsGrid';
import ToolAccessTab from './ToolAccessTab';
import GdprTab from './GdprTab';
import { allowedWriteIsFiledAsProposal } from './proposal-platforms';

import {
  plusSignSVG,
  questionSVG,
  reloadArrowsSVG,
  trashSVG,
  linkSVG,
  chipSVG,
  copySVG,
} from '../../lib/allCustomSVGs';
import CustomSVGs from '../../ui/customSVGs/CustomSVGs';
import { RootState } from '../../store/store';
import { setMessage } from '../../store/reducers/appSlice';
import { updateProfile } from '../../store/reducers/userSlice';
import { fetchAppInit } from '../../lib/api/app-init';
import { can, useEmployeeRole } from '../../lib/rbac';
import {
  McpConnection,
  McpTool,
  ToolPolicy,
  authorizeMcpConnection,
  createMcpConnection,
  deleteMcpConnection,
  listMcpConnections,
  reintrospectMcpConnection,
  setToolPolicy,
} from '../../lib/api/control-center/control-center-api';
import {
  CustomAuthChoice,
  CustomForm,
  buildCustomConnectionPayload,
  buildMcpOauthRedirectUri,
  emptyCustomForm,
  mcpOauthErrorMessageKey,
  parseMcpOauthReturn,
  resolveAuthorizeRedirect,
  stripMcpOauthReturnParams,
  validateCustomForm,
} from './custom-mcp-oauth';
import { APP_ENV } from '../../lib/constants/central-api';
import {
  getConnectorAddress,
  getCustomConnectorAddress,
} from '../../lib/utils/connector-address';
import {
  getCustomOauthAction,
  getCustomOauthPillView,
} from '../integrations/MCP/custom-mcp-oauth-status';
// The credential-derived pill for this tab — mirrors the Platforms tab so the
// same connection reads the same status on both, and keeps introspection health
// (the raw `status` enum) as a secondary, informational signal.
import {
  getGovernanceConnectionStatusView,
  getGovernanceIntrospectionNote,
} from '../integrations/MCP/governance-connection-status';
import {
  McpPillColor,
  countConnectedPlatforms,
} from '../integrations/MCP/mcp-connection-status';
// Page-level platform totals for the stat tiles (same endpoints the `MCP`
// grid uses; the grid keeps its own richer fetch for the card UI).
import get_all_available_mcp_api_call from '../../lib/api/mcp/get-all-available-mcp';
import get_all_connected_mcp_api_call from '../../lib/api/mcp/get-all-connected-mcp';

// ---------- helpers ----------

const POLICY_ORDER: ToolPolicy[] = ['allow', 'require_approval', 'block'];

/**
 * The credential pill palette (`McpPillColor`) mapped onto the three `ccPill`
 * status modifiers this tab styles. Governance only ever produces green / red /
 * yellow, so there is no `blue` case to handle.
 */
const GOVERNANCE_PILL_CLASS: Record<McpPillColor, string> = {
  green: 'ccPill--connected',
  red: 'ccPill--error',
  yellow: 'ccPill--pending',
  blue: 'ccPill--pending',
};

/**
 * The hub tabs, in display order. Default = integrations ("Platforms").
 * Keys are load-bearing: they appear in `?tab=` deep-links (sidebar, the
 * `/control-center` redirect, saved URLs) — relabel freely, never rename.
 */
type ControlCenterTab = 'integrations' | 'governance' | 'tool-access' | 'gdpr';

const TAB_ORDER: ControlCenterTab[] = [
  'integrations',
  'governance',
  // Phase 4 — Tool Access; only rendered for roles with `mcp.access.view`
  // (owner/team_lead/manager) — see `visibleTabs`.
  'tool-access',
  // GDPR — per-MCP PII category settings (agency-wide). Same visibility gate
  // as Tool Access; edits additionally need `mcp.access.manage` (owner/manager),
  // matching the backend write routes.
  'gdpr',
];

function formatTimestamp(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

/** The provider sign-in the user is asked to confirm before leaving the app. */
interface PendingOauthRedirect {
  name: string;
  url: string;
  host: string;
}

const ControlCenter = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const role = useEmployeeRole();
  const canManage = can(role, 'integrations.manage');
  // Phase 4 — Tool Access tab visibility (owner/team_lead/manager) and edit
  // capability (owner/team_lead). Manager sees the grid read-only.
  const canViewToolAccess = can(role, 'mcp.access.view');
  const canManageToolAccess = can(role, 'mcp.access.manage');

  // The tabs this role may actually open. Tool Access is hidden for
  // analyst/read_only; everything else is browsable by anyone scoped here.
  const visibleTabs = useMemo<ControlCenterTab[]>(
    () =>
      TAB_ORDER.filter((tab) =>
        tab === 'tool-access' || tab === 'gdpr' ? canViewToolAccess : true
      ),
    [canViewToolAccess]
  );

  // ----------------------- tab state (URL-synced) -----------------------
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as ControlCenterTab | null;
  // Guard the URL param against tabs this role can't see (deep-link to
  // ?tab=tool-access by an analyst falls back to the default).
  const activeTab: ControlCenterTab =
    tabParam && visibleTabs.includes(tabParam) ? tabParam : 'integrations';

  const selectTab = (tab: ControlCenterTab) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  const activeShopSlug = useSelector(
    (s: RootState) => s.user?.profile?.active_shop_slug
  );
  // The brands this user can govern. Selecting one here is THE way an admin
  // scopes the Control Center per client without the bottom-left switcher.
  const allShops = useSelector(
    (s: RootState) => s.user?.profile?.all_shops || []
  );
  // The control-center-api uses the agency Bearer token. If the user never
  // bridged into the agency portal there is no token to send — skip the
  // network calls rather than surface a 401 banner.
  const agencyToken = useSelector(
    (s: RootState) => s.auth?.agency?.accessToken ?? ''
  );
  // Guards use the boolean, but effects must key on the token VALUE: the
  // admin "login as seller" handoff swaps the agency session mid-page, and a
  // boolean dep would skip the refetch and keep stale wrong-org data.
  const hasAgencyToken = Boolean(agencyToken);
  // The mcp/* platform calls read this slug from the store internally; it is
  // rewritten by `fetchAppInit` after a brand switch, so keying the platform
  // stats effect on it re-counts once the new brand's data is actually live.
  const companyShopSlug = useSelector(
    (s: RootState) => s.user?.company?.shop_slug
  );
  // Bumped on every brand switch.
  const [switching, setSwitching] = useState(false);

  // ----------------------- stat tiles: platform totals -----------------------
  // "X of Y platforms connected" for the tile row. Counted by the same helper
  // from the same row shape as the `MCP` grid; the retired AdUp AI Tracker is
  // never counted. The grid also reports its rows after every reload (connect,
  // update, disconnect), so the tile follows without a page reload.
  const [platformStats, setPlatformStats] = useState<{
    connected: number;
    total: number;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!companyShopSlug) {
      setPlatformStats(null);
      return;
    }
    (async () => {
      try {
        const [all, connected] = await Promise.all([
          get_all_available_mcp_api_call(),
          get_all_connected_mcp_api_call(),
        ]);
        const safeAll = (Array.isArray(all) ? all : []).filter(
          (m: any) => m?.slug !== 'adup_ai_tracking'
        );
        const connMap = new Map(
          (Array.isArray(connected) ? connected : []).map((c: any) => [
            c?.tara_mcp_id,
            c,
          ])
        );
        const rows = safeAll
          .filter(
            (m: any) =>
              (connMap.get(m?.id) as any)?.tara_mcp?.slug !== 'adup_ai_tracking'
          )
          .map((m: any) => {
            const conn: any = connMap.get(m?.id);
            return {
              connection_id: conn?.id,
              is_active: conn?.is_active,
              is_valid: conn?.is_valid,
              is_default: m?.is_default,
              oauth_required: conn?.oauth_required,
              oauth_url: m?.oauth_url,
              accounts: conn?.accounts,
              credentials: conn?.credentials,
            };
          });
        if (!cancelled) {
          setPlatformStats({
            connected: countConnectedPlatforms(rows),
            total: rows.length,
          });
        }
      } catch (err) {
        // Tiles are decorative — never block the page on them.
        // eslint-disable-next-line no-console
        console.warn('[IntegrationsHub] platform stats failed', err);
        if (!cancelled) setPlatformStats(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyShopSlug]);

  // The grid's rows after one of its reloads.
  const handlePlatformsLoaded = useCallback((rows: any[]) => {
    setPlatformStats({
      connected: countConnectedPlatforms(rows),
      total: rows.length,
    });
  }, []);

  /**
   * Brand switcher — sets the page's working brand by dispatching the SAME
   * global active-shop change the sidebar SwitchShop uses
   * (`updateProfile({ active_shop_slug })` + `fetchAppInit(true)`), so the whole
   * app re-scopes to the selected client. The page reads `active_shop_slug`
   * live from the store, so connections/settings all re-fetch via the existing
   * `activeShopSlug` effect once the store updates.
   */
  const handleBrandSwitch = (slug: string) => {
    if (!slug || slug === activeShopSlug) return;
    setSwitching(true);
    dispatch(updateProfile({ active_shop_slug: slug }));
    // Refresh app-init so the rest of the app (sidebar, header, scopes) follows
    // the new active brand — matches SwitchShop's behaviour.
    fetchAppInit(true);
    // The activeShopSlug effect re-runs `refresh()` for the new slug; clear the
    // local switching flag once the store has propagated.
    setTimeout(() => setSwitching(false), 600);
  };

  // ----------------------- data -----------------------
  const [connections, setConnections] = useState<McpConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // 2026-09-07 — true when the connections list was REFUSED (403) rather
  // than empty. The stat tiles used to render that as "0 servers / 0 auto-run",
  // which read as "nothing connected" to a read-only user. Central-api now
  // lets every role with `integrations.view` list connections, but keep the
  // distinction so a genuine permission gap shows as "—", never as zero.
  const [connectionsBlocked, setConnectionsBlocked] = useState(false);

  // ----------------------- per-row in-flight flags -----------------------
  const [reintrospectingId, setReintrospectingId] = useState<
    number | string | null
  >(null);
  // policy mutations in flight, keyed `${connectionId}:${toolName}`
  const [policyBusy, setPolicyBusy] = useState<Set<string>>(() => new Set());

  // ----------------------- add-custom popup -----------------------
  const [customOpen, setCustomOpen] = useState(false);
  const [customForm, setCustomForm] = useState<CustomForm>(emptyCustomForm);
  const [customSubmitting, setCustomSubmitting] = useState(false);
  const [customError, setCustomError] = useState<string | null>(null);
  // OAuth "Advanced" section (client ID / secret / scopes / redirect URI).
  const [customAdvancedOpen, setCustomAdvancedOpen] = useState(false);
  // The redirect URI central-api reported with `client_registration_required`;
  // until then the dialog shows the one built from the API base.
  const [customRedirectUri, setCustomRedirectUri] = useState<string | null>(
    null
  );
  const [redirectUriCopied, setRedirectUriCopied] = useState(false);

  // ----------------------- OAuth redirect confirm -----------------------
  const [oauthRedirect, setOauthRedirect] =
    useState<PendingOauthRedirect | null>(null);
  const [authorizingId, setAuthorizingId] = useState<number | string | null>(
    null
  );
  // The callback result is read once per landing.
  const oauthReturnHandled = useRef(false);

  // ----------------------- disconnect confirm popup -----------------------
  const [disconnectTarget, setDisconnectTarget] =
    useState<McpConnection | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);

  // ----------------------- helpers -----------------------
  const showError = (msg: string) => {
    setError(msg);
    dispatch(setMessage({ messageType: 'error', message: msg }));
  };
  const showSuccess = (msg: string) =>
    dispatch(setMessage({ messageType: 'success', message: msg }));
  const showInfo = (msg: string) =>
    dispatch(setMessage({ messageType: 'info', message: msg }));

  /** Opens a fresh add-custom dialog. */
  const openCustomDialog = () => {
    setCustomForm(emptyCustomForm);
    setCustomError(null);
    setCustomAdvancedOpen(false);
    setCustomRedirectUri(null);
    setRedirectUriCopied(false);
    setCustomOpen(true);
  };

  /**
   * Closes the add-custom dialog and drops everything typed into it, so no
   * bearer or client secret outlives the dialog in component state.
   */
  const closeCustomDialog = () => {
    setCustomOpen(false);
    setCustomForm(emptyCustomForm);
    setCustomError(null);
    setCustomAdvancedOpen(false);
    setCustomRedirectUri(null);
    setRedirectUriCopied(false);
  };

  /**
   * Asks the user to confirm the full-page redirect to the provider's sign-in.
   * Only an https authorize URL is ever followed.
   */
  const confirmOauthRedirect = (
    name: string,
    authorizeUrl: string | null | undefined,
    authorizationServerHost?: string | null
  ): boolean => {
    const target = resolveAuthorizeRedirect(
      authorizeUrl,
      authorizationServerHost
    );
    if (!target) {
      showError(t('ControlCenterOauthStartFailed'));
      return false;
    }
    setOauthRedirect({ name, ...target });
    return true;
  };

  const refresh = useCallback(
    async (shopSlug: string) => {
      setLoading(true);
      setError(null);
      setConnectionsBlocked(false);
      try {
        const conns = await listMcpConnections(shopSlug);
        setConnections(conns);
      } catch (err: any) {
        // eslint-disable-next-line no-console
        console.error('[ControlCenter] load failed', err);
        if (err?.status === 403) {
          setConnectionsBlocked(true);
          showError(t('ControlCenterNoPermission'));
        } else {
          showError(err?.message || t('ControlCenterLoadError'));
        }
        setConnections([]);
      } finally {
        setLoading(false);
      }
    },
    // showError/t are stable enough for this loader; intentionally omitted
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (activeShopSlug && hasAgencyToken) {
      refresh(activeShopSlug);
    } else {
      setConnections([]);
      setLoading(false);
    }
    // reset transient UI when the shop changes
    setCustomOpen(false);
    setCustomForm(emptyCustomForm);
    setCustomAdvancedOpen(false);
    setCustomRedirectUri(null);
    setDisconnectTarget(null);
    setOauthRedirect(null);
  }, [activeShopSlug, agencyToken, refresh]);

  // ----------------------- OAuth return -----------------------
  // central-api's callback lands here with `mcp_oauth=connected|error`
  // (+ `reason`, `connection`). Report it once, take the params out of the URL
  // without a reload, and reload the list. When the agency token is not there
  // yet, the effect above reloads the list as soon as it arrives.
  useEffect(() => {
    if (!searchParams.has('mcp_oauth') || oauthReturnHandled.current) return;
    oauthReturnHandled.current = true;
    const result = parseMcpOauthReturn(searchParams);
    setSearchParams(stripMcpOauthReturnParams(searchParams), { replace: true });
    if (!result) return;
    if (result.outcome === 'connected') {
      showSuccess(t('ControlCenterOauthConnected'));
    } else {
      showError(t(mcpOauthErrorMessageKey(result.reason)));
    }
    if (activeShopSlug && hasAgencyToken) refresh(activeShopSlug);
    // Runs on landing only; the ref keeps a re-render from repeating it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // ----------------------- custom connect -----------------------
  const submitCustom = async () => {
    if (!activeShopSlug || !canManage) return;
    const invalidKey = validateCustomForm(customForm);
    if (invalidKey) {
      setCustomError(t(invalidKey));
      return;
    }
    const payload = buildCustomConnectionPayload(customForm);
    const name = payload.name;

    setCustomSubmitting(true);
    setCustomError(null);
    try {
      const conn = await createMcpConnection(activeShopSlug, payload);
      if (payload.auth_type === 'mcp_oauth') {
        // Saved as `pending`, nothing introspected yet: the user signs in at
        // the provider next. Cancelling that leaves the row, listed as
        // "Authorization required" with a Connect action.
        closeCustomDialog();
        selectTab('governance');
        await refresh(activeShopSlug);
        if (conn.oauth?.authorize_url) {
          confirmOauthRedirect(
            name,
            conn.oauth.authorize_url,
            conn.oauth.authorization_server_host
          );
        } else {
          showInfo(t('ControlCenterOauthAuthorizeLater', { name }));
        }
        return;
      }
      if (conn.status === 'error') {
        // introspection failed — surface the server's reason in the form so
        // the user can fix the URL/secret without losing context
        setCustomError(conn.last_error || t('ControlCenterIntrospectFailed'));
        await refresh(activeShopSlug);
        return;
      }
      closeCustomDialog();
      showSuccess(t('ControlCenterConnected', { name }));
      // A freshly added server appears in Governance — nudge the user there.
      selectTab('governance');
      await refresh(activeShopSlug);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('[ControlCenter] custom connect failed', err);
      if (err?.status === 422 && err?.code === 'client_registration_required') {
        // The server can't register Tara automatically: open the client
        // fields and keep the dialog as it is.
        setCustomAdvancedOpen(true);
        if (err?.redirect_uri) setCustomRedirectUri(err.redirect_uri);
        setCustomError(err?.message || t('ControlCenterOauthClientRequired'));
      } else if (err?.status === 422 && err?.validation) {
        const details = Object.entries(
          err.validation as Record<string, string[]>
        )
          .map(([k, msgs]) => `${k}: ${msgs.join(', ')}`)
          .join(' · ');
        setCustomError(`${t('ControlCenterValidationFailed')} — ${details}`);
      } else {
        setCustomError(err?.message || t('ControlCenterConnectFailed'));
      }
    } finally {
      setCustomSubmitting(false);
    }
  };

  // ----------------------- OAuth connect / reconnect -----------------------
  const handleAuthorize = async (conn: McpConnection) => {
    if (!activeShopSlug || !canManage) return;
    setAuthorizingId(conn.id);
    try {
      const auth = await authorizeMcpConnection(activeShopSlug, conn.id);
      confirmOauthRedirect(
        conn.name,
        auth?.authorize_url,
        auth?.authorization_server_host
      );
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('[ControlCenter] oauth authorize failed', err);
      showError(err?.message || t('ControlCenterOauthStartFailed'));
    } finally {
      setAuthorizingId(null);
    }
  };

  // The address that serves this brand's custom servers; the base connector
  // does not list their tools.
  const customServersUrl = getCustomConnectorAddress(
    getConnectorAddress(APP_ENV)
  );
  const [customAddressCopied, setCustomAddressCopied] = useState(false);
  const copyCustomAddress = async () => {
    try {
      await navigator.clipboard.writeText(customServersUrl);
      setCustomAddressCopied(true);
      setTimeout(() => setCustomAddressCopied(false), 2000);
    } catch {
      showError(t('McpSetup_CopyFailed'));
    }
  };

  const copyRedirectUri = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setRedirectUriCopied(true);
      setTimeout(() => setRedirectUriCopied(false), 2000);
    } catch {
      showError(t('ControlCenterOauthCopyFailed'));
    }
  };

  // ----------------------- reintrospect -----------------------
  const handleReintrospect = async (conn: McpConnection) => {
    if (!activeShopSlug || !canManage) return;
    setReintrospectingId(conn.id);
    setError(null);
    try {
      const updated = await reintrospectMcpConnection(activeShopSlug, conn.id);
      if (updated.status === 'error') {
        showError(updated.last_error || t('ControlCenterIntrospectFailed'));
      } else {
        showSuccess(t('ControlCenterReintrospected', { name: conn.name }));
      }
      await refresh(activeShopSlug);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('[ControlCenter] reintrospect failed', err);
      showError(err?.message || t('ControlCenterReintrospectFailed'));
    } finally {
      setReintrospectingId(null);
    }
  };

  // ----------------------- disconnect -----------------------
  const handleConfirmDisconnect = async () => {
    if (!activeShopSlug || !disconnectTarget) return;
    setDisconnecting(true);
    try {
      await deleteMcpConnection(activeShopSlug, disconnectTarget.id);
      showInfo(t('ControlCenterDisconnected', { name: disconnectTarget.name }));
      setDisconnectTarget(null);
      await refresh(activeShopSlug);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('[ControlCenter] disconnect failed', err);
      showError(err?.message || t('ControlCenterDisconnectFailed'));
    } finally {
      setDisconnecting(false);
    }
  };

  // ----------------------- policy change (optimistic) -----------------------
  const handlePolicyChange = async (
    conn: McpConnection,
    tool: McpTool,
    next: ToolPolicy
  ) => {
    if (!activeShopSlug || !canManage) return;
    if (tool.policy === next) return;
    const busyKey = `${conn.id}:${tool.tool_name}`;
    const previous = tool.policy;

    // optimistic update
    setConnections((prev) =>
      prev.map((c) =>
        c.id === conn.id
          ? {
              ...c,
              tools: c.tools.map((tl) =>
                tl.tool_name === tool.tool_name ? { ...tl, policy: next } : tl
              ),
            }
          : c
      )
    );
    setPolicyBusy((prev) => new Set(prev).add(busyKey));

    try {
      await setToolPolicy(activeShopSlug, conn.id, tool.tool_name, next);
      showSuccess(
        t('ControlCenterPolicyUpdated', { tool: tool.tool_name })
      );
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('[ControlCenter] policy update failed', err);
      // roll back the optimistic change
      setConnections((prev) =>
        prev.map((c) =>
          c.id === conn.id
            ? {
                ...c,
                tools: c.tools.map((tl) =>
                  tl.tool_name === tool.tool_name
                    ? { ...tl, policy: previous }
                    : tl
                ),
              }
            : c
        )
      );
      showError(err?.message || t('ControlCenterPolicyFailed'));
    } finally {
      setPolicyBusy((prev) => {
        const nextSet = new Set(prev);
        nextSet.delete(busyKey);
        return nextSet;
      });
    }
  };

  // ----------------------- render: policy segmented control -----------------------
  const renderPolicyToggle = (conn: McpConnection, tool: McpTool) => {
    const busyKey = `${conn.id}:${tool.tool_name}`;
    const busy = policyBusy.has(busyKey);
    // On the ad platforms an allowed write does not run on its own.
    const allowHelp = allowedWriteIsFiledAsProposal(conn.platform_key)
      ? t('ControlCenterAllowFiledAsProposal')
      : undefined;
    const segmentMeta: Record<
      ToolPolicy,
      { cls: string; label: string }
    > = {
      allow: { cls: 'allow', label: t('ControlCenterPolicyAllow') },
      require_approval: {
        cls: 'approval',
        label: t('ControlCenterPolicyApproval'),
      },
      block: { cls: 'block', label: t('ControlCenterPolicyBlock') },
    };

    return (
      <div
        className='ccPolicyToggle'
        data-busy={busy ? 'true' : 'false'}
        role='radiogroup'
        aria-label={t('ControlCenterPolicyFor', { tool: tool.tool_name })}
      >
        {POLICY_ORDER.map((p) => {
          const meta = segmentMeta[p];
          const active = tool.policy === p;
          return (
            <button
              key={p}
              type='button'
              role='radio'
              aria-checked={active}
              data-active={active ? 'true' : 'false'}
              className={`ccPolicyToggle__segment ccPolicyToggle__segment--${meta.cls}`}
              title={p === 'allow' ? allowHelp : undefined}
              disabled={!canManage || busy}
              onClick={() => handlePolicyChange(conn, tool, p)}
            >
              {meta.label}
            </button>
          );
        })}
      </div>
    );
  };

  // ----------------------- render: a connection card -----------------------
  const renderConnection = (conn: McpConnection) => {
    // Prefer the credential-derived pill (same vocabulary as the Platforms
    // tab). It returns null for custom servers and for older payloads without
    // the additive credential fields — those fall back to the raw
    // introspection `status` enum exactly as before.
    const credentialView = getGovernanceConnectionStatusView(conn);
    // A custom OAuth server that still needs (or lost) its authorization says
    // so; an authorized one reads its introspection status like any other.
    const oauthPill = getCustomOauthPillView(conn);
    const oauthAction = getCustomOauthAction(conn);
    const pillView = oauthPill || credentialView;
    const statusCls = pillView
      ? GOVERNANCE_PILL_CLASS[pillView.color]
      : conn.status === 'connected'
      ? 'ccPill--connected'
      : conn.status === 'error'
      ? 'ccPill--error'
      : 'ccPill--pending';
    const statusLabel = pillView
      ? t(pillView.labelKey)
      : conn.status === 'connected'
      ? t('ControlCenterStatusConnected')
      : conn.status === 'error'
      ? t('ControlCenterStatusError')
      : t('ControlCenterStatusPending');

    // Introspection health is secondary. On the credential-derived path a
    // failed re-scan is a soft, informational note (the credential is fine, its
    // tool list just could not be refreshed) — never a hard red Error. Only the
    // introspection fallback path (custom / no credential signal) keeps the
    // hard error note.
    const introspectNote = getGovernanceIntrospectionNote(conn);

    const readTools = conn.tools.filter((tl) => tl.classification === 'read');
    const writeTools = conn.tools.filter((tl) => tl.classification === 'write');
    const isReintrospecting = reintrospectingId === conn.id;
    const isAuthorizing = authorizingId === conn.id;
    const oauthHost = conn.oauth?.authorization_server_host;
    const oauthAuthorizedBy =
      conn.oauth?.status === 'authorized' ? conn.oauth?.authorized_by : null;

    return (
      <div className='ccConnection' key={String(conn.id)}>
        <div className='ccConnection__header'>
          <div className='ccConnection__headerLeft'>
            <h3 className='ccConnection__name'>
              {platformDisplayName(conn.platform_key, conn.name, t)}
              <span className={`ccPill ${statusCls}`}>{statusLabel}</span>
              <span className='ccPill ccPill--type'>
                {conn.type === 'catalog'
                  ? t('ControlCenterTypeCatalog')
                  : t('ControlCenterTypeCustom')}
              </span>
            </h3>
            {conn.url && <span className='ccConnection__url'>{conn.url}</span>}
            {conn.oauth && (oauthAuthorizedBy || oauthHost) && (
              <span className='ccConnection__meta'>
                {oauthAuthorizedBy
                  ? t('ControlCenterOauthAuthorizedBy', {
                      name: oauthAuthorizedBy,
                    })
                  : t('ControlCenterAuthOauth')}
                {oauthHost
                  ? ` · ${t('ControlCenterOauthSignInHost', {
                      host: oauthHost,
                    })}`
                  : ''}
              </span>
            )}
            {conn.last_introspected_at && (
              <span className='ccConnection__meta'>
                {t('ControlCenterLastIntrospected')}:{' '}
                {formatTimestamp(conn.last_introspected_at)}
              </span>
            )}
          </div>

          {canManage && (
            <div className='ccConnection__actions'>
              {oauthAction ? (
                // No token yet (or a dead one): a re-scan can't succeed, so
                // the card offers the sign-in instead.
                <button
                  type='button'
                  className='ccConnection__iconBtn'
                  disabled={isAuthorizing}
                  onClick={() => handleAuthorize(conn)}
                >
                  <CustomSVGs svg={linkSVG} />
                  {isAuthorizing
                    ? t('ControlCenterOauthStarting')
                    : oauthAction === 'reconnect'
                    ? t('ControlCenterOauthReconnect')
                    : t('ControlCenterOauthConnect')}
                </button>
              ) : (
                <button
                  type='button'
                  className='ccConnection__iconBtn'
                  disabled={isReintrospecting}
                  onClick={() => handleReintrospect(conn)}
                >
                  <CustomSVGs svg={reloadArrowsSVG} />
                  {isReintrospecting
                    ? t('ControlCenterReintrospecting')
                    : t('ControlCenterReintrospect')}
                </button>
              )}
              <button
                type='button'
                className='ccConnection__iconBtn ccConnection__iconBtn--danger'
                onClick={() => setDisconnectTarget(conn)}
              >
                <CustomSVGs svg={trashSVG} />
                {t('ControlCenterDisconnect')}
              </button>
            </div>
          )}
        </div>

        {introspectNote === 'hard' && (
          <div className='ccConnection__errorNote'>
            <strong>{t('ControlCenterIntrospectFailed')}:</strong>{' '}
            {conn.last_error}
          </div>
        )}

        {/* Soft, informational variant: the credential is healthy, so the
            failed re-scan is surfaced in neutral sub-text (not the red error
            box) — the tool list below may simply be out of date. */}
        {introspectNote === 'soft' && (
          <div className='ccConnection__meta'>
            <strong>{t('ControlCenterIntrospectDegraded')}:</strong>{' '}
            {conn.last_error}
          </div>
        )}

        {/* Read tools — always allowed, not editable */}
        <div className='ccConnection__toolGroup'>
          <div className='ccConnection__toolGroupTitle'>
            {t('ControlCenterReadTools')} <span>({readTools.length})</span>
          </div>
          {readTools.length === 0 ? (
            <div className='ccConnection__noTools'>
              {t('ControlCenterNoReadTools')}
            </div>
          ) : (
            readTools.map((tool) => (
              <div className='ccTool' key={tool.tool_name}>
                <div className='ccTool__info'>
                  <span className='ccTool__name'>{tool.tool_name}</span>
                  {tool.description && (
                    <span className='ccTool__desc'>{tool.description}</span>
                  )}
                </div>
                <span className='ccTool__allowed'>
                  {t('ControlCenterAllowed')}
                </span>
              </div>
            ))
          )}
        </div>

        <hr className='ccConnection__divider' />

        {/* Write tools — 3-way policy control */}
        <div className='ccConnection__toolGroup'>
          <div className='ccConnection__toolGroupTitle'>
            {t('ControlCenterWriteTools')} <span>({writeTools.length})</span>
          </div>
          {writeTools.length > 0 &&
            allowedWriteIsFiledAsProposal(conn.platform_key) && (
              <p className='ccConnection__allowHint'>
                {t('ControlCenterAllowFiledAsProposal')}
              </p>
            )}
          {writeTools.length === 0 ? (
            <div className='ccConnection__noTools'>
              {t('ControlCenterNoWriteTools')}
            </div>
          ) : (
            writeTools.map((tool) => (
              <div className='ccTool' key={tool.tool_name}>
                <div className='ccTool__info'>
                  <span className='ccTool__name'>{tool.tool_name}</span>
                  {tool.description && (
                    <span className='ccTool__desc'>{tool.description}</span>
                  )}
                </div>
                {renderPolicyToggle(conn, tool)}
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  // ----------------------- render: brand switcher -----------------------
  // THE way an admin governs per client without the bottom-left switcher.
  // Dropdown of the user's accessible brands; selecting one re-scopes the
  // whole app (and this page) to that brand. Shared by ALL tabs.
  const renderBrandSwitcher = () => {
    if (!allShops || allShops.length === 0) return null;
    return (
      <div className='controlCenter__brandBar'>
        <label className='controlCenter__brandBar-label' htmlFor='cc-brand-select'>
          {t('ControlCenterBrandLabel')}
        </label>
        <div className='controlCenter__brandBar-selectWrap'>
          <select
            id='cc-brand-select'
            className='controlCenter__brandBar-select'
            value={activeShopSlug || ''}
            disabled={switching}
            onChange={(e) => handleBrandSwitch(e.target.value)}
            aria-label={t('ControlCenterBrandLabel')}
          >
            {!activeShopSlug && (
              <option value='' disabled>
                {t('ControlCenterSelectBrand')}
              </option>
            )}
            {allShops.map((sh) => (
              <option key={sh.slug} value={sh.slug}>
                {sh.name}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  };

  // ----------------------- render: stat tiles -----------------------
  // Page-level summary row in the Team-page tile language (`.teamStats`):
  // platforms connected, MCP servers, and the write-tool policy split. Counts
  // come from the page-level fetches, so they are live on every tab.
  const renderStatTiles = () => {
    if (!activeShopSlug || !hasAgencyToken) return null;

    // An allowed write runs on its own, except on the ad platforms, where it
    // is filed as a proposal first: those count as gated.
    const writeToolCount = connections.reduce(
      (sum, c) =>
        sum + (c.tools || []).filter((tl) => tl.classification === 'write').length,
      0
    );
    const autoRun = connections
      .filter((c) => !allowedWriteIsFiledAsProposal(c.platform_key))
      .flatMap((c) => c.tools || [])
      .filter((tl) => tl.classification === 'write' && tl.policy === 'allow')
      .length;
    const gated = writeToolCount - autoRun; // require_approval + block + filed

    return (
      <div className='ccStats'>
        <div className='ccStat'>
          <span
            className='ccStat__v'
            data-testid='controlCenter-platformsConnected'
          >
            {platformStats
              ? `${platformStats.connected}/${platformStats.total}`
              : '—'}
          </span>
          <span className='ccStat__l'>{t('IntegrationsStatPlatforms')}</span>
        </div>
        <div
          className='ccStat'
          title={connectionsBlocked ? t('ControlCenterNoPermission') : undefined}
        >
          <span className='ccStat__v'>
            {connectionsBlocked ? '—' : connections.length}
          </span>
          <span className='ccStat__l'>{t('IntegrationsStatServers')}</span>
        </div>
        <div className='ccStat'>
          <span className='ccStat__v'>{connectionsBlocked ? '—' : autoRun}</span>
          <span className='ccStat__l'>{t('IntegrationsStatAutoRun')}</span>
        </div>
        <div className='ccStat'>
          <span
            className={`ccStat__v ${
              !connectionsBlocked && gated ? 'ccStat__v--amber' : ''
            }`}
          >
            {connectionsBlocked ? '—' : gated}
          </span>
          <span className='ccStat__l'>{t('IntegrationsStatGated')}</span>
        </div>
      </div>
    );
  };

  // ----------------------- render: tab strip -----------------------
  // Labels only — the keys stay `integrations`/`governance`/`tool-access` for
  // deep-link compatibility (see TAB_ORDER note above).
  const TAB_META: Record<ControlCenterTab, string> = {
    integrations: t('IntegrationsTabPlatforms'),
    governance: t('IntegrationsTabMcpServers'),
    'tool-access': t('ControlCenterTabToolAccess'),
    gdpr: t('IntegrationsTabGdpr'),
  };

  const renderTabStrip = () => (
    <div className='controlCenterTabs' role='tablist'>
      {visibleTabs.map((tab) => (
        <button
          key={tab}
          type='button'
          role='tab'
          aria-selected={activeTab === tab}
          className={`controlCenterTabs__tab ${
            activeTab === tab ? 'controlCenterTabs__tab--active' : ''
          }`}
          onClick={() => selectTab(tab)}
        >
          {TAB_META[tab]}
        </button>
      ))}
    </div>
  );

  // ----------------------- render: Integrations tab -----------------------
  // Full per-platform connect grid (reused from the old Integrations page) +
  // the Meta System-User token flow folded in beneath it.
  const renderIntegrationsTab = () => (
    <div className='controlCenter__tabBody'>
      {/* The original Integrations Hub UI — real platform-logo cards with the
          shared search + status filter (the `MCP` grid). Hosted here at
          /integrations so the per-platform OAuth callback round-trips back to
          this exact page. The Meta System-User connect CTA is embedded
          directly on the Facebook card inside the grid (see MCP.tsx) — there is
          no separate System-User section any more. */}
      <IntegrationsGrid onPlatformsLoaded={handlePlatformsLoaded} />
    </div>
  );

  // ----------------------- render: MCP Servers tab -----------------------
  // The connected-servers list + per-write-tool policy UI. The old hidden
  // "Custom MCP" tab is folded in here: its add-by-URL flow is the header
  // action (and the empty state's CTA), so connecting and governing servers
  // live on one surface.
  const renderGovernanceTab = () => (
    <div className='controlCenter__tabBody'>
      <div className='controlCenter__section'>
        <div className='controlCenter__sectionHeader'>
          <div className='controlCenter__sectionHeader-text'>
            <h2>{t('ControlCenterConnectedTitle')}</h2>
            <p>{t('ControlCenterConnectedSubtitle')}</p>
          </div>
          {canManage && (
            <MainSquareButton
              type='button'
              value={t('ControlCenterAddCustom')}
              icon={plusSignSVG}
              buttonType='white'
              normal={true}
              onClick={openCustomDialog}
            />
          )}
        </div>
        {connections.some((c) => c.type === 'custom') && (
          <div
            className='controlCenter__customAddress'
            data-testid='controlCenter-customAddress'
          >
            <div className='controlCenter__customAddress-row'>
              <strong>{t('CustomServersAddress_Label')}</strong>
              <code>{customServersUrl}</code>
              <button
                type='button'
                className='ccConnection__iconBtn'
                aria-label={t('CustomServersAddress_Copy')}
                onClick={copyCustomAddress}
              >
                <CustomSVGs svg={copySVG} />
                {customAddressCopied ? t('Copied') : t('Copy')}
              </button>
            </div>
            <p>{t('CustomServersAddress_Note')}</p>
          </div>
        )}
        {connections.length === 0 ? (
          <TeachingEmptyState
            icon={chipSVG}
            title={t('ControlCenterEmptyTitle')}
            body={t('ControlCenterEmptyBody')}
            actionLabel={canManage ? t('ControlCenterEmptyAction') : undefined}
            onAction={canManage ? openCustomDialog : undefined}
          />
        ) : (
          connections.map(renderConnection)
        )}
      </div>
    </div>
  );

  // ----------------------- render: active tab body -----------------------
  const renderActiveTab = () => {
    switch (activeTab) {
      case 'governance':
        return renderGovernanceTab();
      case 'tool-access':
        // Self-contained — owns its own data fetch, loading + empty states.
        // The page-level connections fetch/spinner does not apply here.
        return (
          <ToolAccessTab
            activeShopSlug={activeShopSlug}
            agencyToken={agencyToken}
            canManage={canManageToolAccess}
            showError={showError}
            showSuccess={showSuccess}
            showInfo={showInfo}
          />
        );
      case 'gdpr':
        // Self-contained — owns its own data fetch, loading + error states.
        // Agency-wide (org-level) settings: the brand bar does not scope it.
        return (
          <GdprTab
            agencyToken={agencyToken}
            canManage={canManageToolAccess}
            showError={showError}
            showSuccess={showSuccess}
          />
        );
      case 'integrations':
      default:
        return renderIntegrationsTab();
    }
  };

  // ----------------------- render: the page chrome -----------------------
  const renderBody = () => {
    if (!activeShopSlug) {
      return (
        <div className='controlCenter__banner'>
          {t('ControlCenterSelectBrand')}
        </div>
      );
    }
    if (!hasAgencyToken) {
      return (
        <div className='controlCenter__banner'>
          {t('ControlCenterNoAgencyToken')}
        </div>
      );
    }

    return (
      <div className='controlCenter'>
        {error && (
          <div className='controlCenter__error'>
            <span>{error}</span>
            <button
              type='button'
              className='controlCenter__error--close'
              onClick={() => setError(null)}
              aria-label='Dismiss error'
            >
              ×
            </button>
          </div>
        )}

        {!canManage && (
          <div className='controlCenter__readonly'>
            {t('ControlCenterReadOnly')}
          </div>
        )}

        {/* The MCP Servers tab depends on the connections fetch; show a
            spinner only when that fetch is in flight and we have nothing yet. */}
        {loading && connections.length === 0 && activeTab === 'governance' ? (
          <LoadingSpinner />
        ) : (
          renderActiveTab()
        )}
      </div>
    );
  };

  return (
    <PageWrapperMain>
      <div className='subPage'>
        <SubPageContentHolder>
          <SubPageInnerContentHolder
            title={t('IntegrationsHub')}
            subTitle={t('IntegrationsHubSubTitle')}
            backgroundColor={'var(--main-background-color)'}
            mainButtonType='white'
            mainBtnTitle={t('Help')}
            mainBtnIcon={questionSVG}
            mainBtnLink={
              'https://intercom.help/ad-up/en/collections/13474403-adup-settings'
            }
            isMainBtnLinkExternal={true}
          >
            {/* Brand bar — applies to ALL tabs. */}
            {renderBrandSwitcher()}
            {/* Stat tiles — page-level summary, visible on every tab. */}
            {renderStatTiles()}
            {/* Tab strip. */}
            {renderTabStrip()}
            {renderBody()}
          </SubPageInnerContentHolder>
        </SubPageContentHolder>
      </div>

      {/* ---------- Add-custom popup ---------- */}
      <Popup
        styles={{ backgroundColor: 'var(--containers-background-color)' }}
        trigger={customOpen}
        setTrigger={() => {
          if (!customSubmitting) closeCustomDialog();
        }}
      >
        <div className='ccPopup'>
          <h2 className='ccPopup__title'>{t('ControlCenterAddCustom')}</h2>
          <span className='ccPopup__subtitle'>
            {t('ControlCenterCustomPopupSubtitle')}
          </span>

          <div className='ccPopup__form'>
            <div className='ccPopup__field'>
              <label htmlFor='cc-custom-name'>
                {t('ControlCenterFieldName')}
              </label>
              <input
                id='cc-custom-name'
                type='text'
                value={customForm.name}
                placeholder={t('ControlCenterFieldNamePlaceholder')}
                disabled={customSubmitting}
                onChange={(e) =>
                  setCustomForm((p) => ({ ...p, name: e.target.value }))
                }
              />
            </div>

            <div className='ccPopup__field'>
              <label htmlFor='cc-custom-url'>
                {t('ControlCenterFieldUrl')}
              </label>
              <input
                id='cc-custom-url'
                type='url'
                value={customForm.url}
                placeholder='https://mcp.example.com/sse'
                disabled={customSubmitting}
                onChange={(e) =>
                  setCustomForm((p) => ({ ...p, url: e.target.value }))
                }
              />
            </div>

            <div className='ccPopup__field'>
              <label htmlFor='cc-custom-auth'>
                {t('ControlCenterFieldAuthType')}
              </label>
              <select
                id='cc-custom-auth'
                value={customForm.auth_type}
                disabled={customSubmitting}
                onChange={(e) =>
                  setCustomForm((p) => ({
                    ...p,
                    auth_type: e.target.value as CustomAuthChoice,
                  }))
                }
              >
                <option value='none'>{t('ControlCenterAuthNone')}</option>
                <option value='bearer'>{t('ControlCenterAuthBearer')}</option>
                <option value='mcp_oauth'>{t('ControlCenterAuthOauth')}</option>
              </select>
            </div>

            {customForm.auth_type === 'bearer' && (
              <div className='ccPopup__field'>
                <label htmlFor='cc-custom-secret'>
                  {t('ControlCenterFieldSecret')}
                </label>
                <input
                  id='cc-custom-secret'
                  type='password'
                  autoComplete='off'
                  value={customForm.auth_secret}
                  placeholder={t('ControlCenterFieldSecretPlaceholder')}
                  disabled={customSubmitting}
                  onChange={(e) =>
                    setCustomForm((p) => ({
                      ...p,
                      auth_secret: e.target.value,
                    }))
                  }
                />
              </div>
            )}

            {customForm.auth_type === 'mcp_oauth' && (
              <>
                <p className='ccPlugin__hint'>{t('ControlCenterOauthHelp')}</p>

                <div>
                  <button
                    type='button'
                    className='ccConnection__iconBtn'
                    aria-expanded={customAdvancedOpen}
                    aria-controls='cc-custom-oauth-advanced'
                    onClick={() => setCustomAdvancedOpen((open) => !open)}
                  >
                    {customAdvancedOpen ? '▾' : '▸'}{' '}
                    {t('ControlCenterOauthAdvanced')}
                  </button>
                </div>

                {customAdvancedOpen && (
                  <div
                    id='cc-custom-oauth-advanced'
                    className='ccPopup__form'
                    style={{ marginBottom: 0 }}
                  >
                    <p className='ccPlugin__hint'>
                      {t('ControlCenterOauthAdvancedHint')}
                    </p>

                    <div className='ccPopup__field'>
                      <label htmlFor='cc-custom-oauth-redirect'>
                        {t('ControlCenterOauthRedirectUri')}
                      </label>
                      <div style={{ display: 'flex', gap: '0.8rem' }}>
                        <input
                          id='cc-custom-oauth-redirect'
                          type='text'
                          readOnly
                          style={{ flex: 1, minWidth: 0 }}
                          value={
                            customRedirectUri || buildMcpOauthRedirectUri()
                          }
                          onFocus={(e) => e.target.select()}
                        />
                        <button
                          type='button'
                          className='ccConnection__iconBtn'
                          onClick={() =>
                            copyRedirectUri(
                              customRedirectUri || buildMcpOauthRedirectUri()
                            )
                          }
                        >
                          <CustomSVGs svg={copySVG} />
                          {redirectUriCopied ? t('Copied') : t('Copy')}
                        </button>
                      </div>
                    </div>

                    <div className='ccPopup__field'>
                      <label htmlFor='cc-custom-oauth-client-id'>
                        {t('ControlCenterOauthClientId')}
                      </label>
                      <input
                        id='cc-custom-oauth-client-id'
                        type='text'
                        autoComplete='off'
                        value={customForm.oauth_client_id}
                        disabled={customSubmitting}
                        onChange={(e) =>
                          setCustomForm((p) => ({
                            ...p,
                            oauth_client_id: e.target.value,
                          }))
                        }
                      />
                    </div>

                    <div className='ccPopup__field'>
                      <label htmlFor='cc-custom-oauth-client-secret'>
                        {t('ControlCenterOauthClientSecret')}
                      </label>
                      <input
                        id='cc-custom-oauth-client-secret'
                        type='password'
                        autoComplete='new-password'
                        value={customForm.oauth_client_secret}
                        placeholder={t('ControlCenterOauthOptional')}
                        disabled={customSubmitting}
                        onChange={(e) =>
                          setCustomForm((p) => ({
                            ...p,
                            oauth_client_secret: e.target.value,
                          }))
                        }
                      />
                    </div>

                    <div className='ccPopup__field'>
                      <label htmlFor='cc-custom-oauth-scopes'>
                        {t('ControlCenterOauthScopes')}
                      </label>
                      <input
                        id='cc-custom-oauth-scopes'
                        type='text'
                        autoComplete='off'
                        value={customForm.oauth_scopes}
                        placeholder={t('ControlCenterOauthScopesPlaceholder')}
                        disabled={customSubmitting}
                        onChange={(e) =>
                          setCustomForm((p) => ({
                            ...p,
                            oauth_scopes: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>
                )}
              </>
            )}

            {customError && (
              <div className='ccPopup__formError'>{customError}</div>
            )}
          </div>

          <div className='ccPopup__actions'>
            <MainSquareButton
              value={t('Cancel')}
              buttonType='white'
              type='button'
              normal={true}
              disabled={customSubmitting}
              onClick={closeCustomDialog}
            />
            <MainSquareButton
              value={t('ControlCenterAddServer')}
              icon={linkSVG}
              type='button'
              normal={true}
              loading={customSubmitting}
              disabled={customSubmitting}
              onClick={submitCustom}
            />
          </div>
        </div>
      </Popup>

      {/* ---------- Disconnect confirm popup ---------- */}
      <Popup
        styles={{ backgroundColor: 'var(--containers-background-color)' }}
        trigger={!!disconnectTarget}
        setTrigger={() => {
          if (!disconnecting) setDisconnectTarget(null);
        }}
      >
        <div className='ccPopup'>
          <h2 className='ccPopup__title'>
            {t('ControlCenterDisconnectTitle')}
          </h2>
          <div className='ccPopup__confirmBody'>
            {t('ControlCenterDisconnectBody')}{' '}
            <strong>{disconnectTarget?.name}</strong>
            {activeShopSlug ? (
              <>
                {' '}
                ({activeShopSlug}).
              </>
            ) : (
              '.'
            )}
          </div>
          <div className='ccPopup__actions'>
            <MainSquareButton
              value={t('Cancel')}
              buttonType='white'
              type='button'
              normal={true}
              disabled={disconnecting}
              onClick={() => setDisconnectTarget(null)}
            />
            <MainSquareButton
              value={t('ControlCenterDisconnect')}
              icon={trashSVG}
              buttonType='danger'
              type='button'
              normal={true}
              loading={disconnecting}
              disabled={disconnecting}
              onClick={handleConfirmDisconnect}
            />
          </div>
        </div>
      </Popup>

      {/* ---------- OAuth redirect confirm ----------
          Leaving the app for the provider's sign-in is a full-page navigation:
          confirm it first and name the host the browser will go to. Same copy
          and layout as the Platforms tab's OAuth confirmation. Cancelling
          keeps the saved row, which the list shows as "Authorization
          required" with a Connect action. */}
      <Popup
        styles={{ backgroundColor: 'var(--containers-background-color)' }}
        trigger={!!oauthRedirect}
        setTrigger={() => setOauthRedirect(null)}
      >
        <div className='ccPopup'>
          <h2 className='ccPopup__title'>
            {t('ControlCenterOauthConfirmTitle', {
              host: oauthRedirect?.host,
            })}
          </h2>
          <div className='ccPopup__confirmBody'>
            {t('ControlCenterOauthConfirmText', {
              host: oauthRedirect?.host,
              name: oauthRedirect?.name,
            })}
          </div>
          <div className='ccPopup__actions'>
            <MainSquareButton
              value={t('Cancel')}
              buttonType='white'
              type='button'
              normal={true}
              onClick={() => setOauthRedirect(null)}
            />
            <MainSquareButton
              value={t('MCP-OauthRedirectConfirmContinue')}
              icon={linkSVG}
              type='button'
              normal={true}
              onClick={() => {
                const target = oauthRedirect;
                setOauthRedirect(null);
                if (target) window.location.href = target.url;
              }}
            />
          </div>
        </div>
      </Popup>
    </PageWrapperMain>
  );
};

export default ControlCenter;
